import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { loyaltyProgramInput } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { MaskedInput } from '@/components/shared/masked-input';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { centsToInput, parseMoney } from '@/lib/format';
import type { LoyaltyProgram } from './api';
import { applyServerError, zodForm } from './lib';

interface Values {
  mode: 'points' | 'visits';
  active: boolean;
  pointsPerReal: string;
  rewardThreshold: string;
  rewardDescription: string;
  rewardValue: string;
}

const toPayload = (v: Values) => ({
  mode: v.mode,
  active: v.active,
  pointsPerReal: v.mode === 'points' ? Number(v.pointsPerReal) : 1,
  rewardThreshold: Number(v.rewardThreshold),
  rewardDescription: v.rewardDescription,
  rewardValueCents: parseMoney(v.rewardValue),
});

export function ProgramTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['loyalty', 'program'], queryFn: () => api.get<LoyaltyProgram>('/loyalty/program') });
  const form = useForm<Values>({
    resolver: zodForm<Values>(loyaltyProgramInput, toPayload, { rewardValueCents: 'rewardValue' }),
    defaultValues: { mode: 'visits', active: false, pointsPerReal: '1', rewardThreshold: '10', rewardDescription: 'Corte grátis', rewardValue: '0,00' },
  });
  const { errors } = form.formState;
  useEffect(() => {
    if (!q.data) return;
    form.reset({
      mode: q.data.mode,
      active: q.data.active,
      pointsPerReal: String(q.data.pointsPerReal),
      rewardThreshold: String(q.data.rewardThreshold),
      rewardDescription: q.data.rewardDescription,
      rewardValue: centsToInput(q.data.rewardValueCents),
    });
  }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: Values) => api.put('/loyalty/program', loyaltyProgramInput.parse(toPayload(v))),
    onSuccess: () => {
      toast.success('Programa de fidelidade salvo');
      qc.invalidateQueries({ queryKey: ['loyalty'] });
      qc.invalidateQueries({ queryKey: ['customers', 'loyalty'] });
    },
    onError: (e) => applyServerError(form, e),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const mode = form.watch('mode');

  return (
    <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid max-w-xl gap-5" noValidate>
      <div className="flex items-start gap-3 rounded-lg border bg-card p-4">
        <Controller control={form.control} name="active" render={({ field }) => <Switch id="prog-active" checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />} />
        <div>
          <label htmlFor="prog-active" className="text-sm font-medium">
            Programa de fidelidade ativo
          </label>
          <p className="text-xs text-muted-foreground">Quando ativo, cada pagamento confirmado de um cliente cadastrado soma ao saldo dele. Desativar não apaga saldos.</p>
        </div>
      </div>

      <Field id="prog-mode" label="Como o cliente acumula" error={errors.mode?.message}>
        <Controller
          control={form.control}
          name="mode"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger id="prog-mode" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="visits">1 ponto por visita paga</SelectItem>
                <SelectItem value="points">Pontos por real pago</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </Field>
      {mode === 'points' && (
        <Field id="prog-ppr" label="Pontos por R$ 1,00 pago" hint="Valores quebrados são arredondados para baixo. R$ 30,50 com 1 ponto por real = 30 pontos." error={errors.pointsPerReal?.message}>
          <Input id="prog-ppr" inputMode="numeric" className="max-w-32" {...form.register('pointsPerReal')} />
        </Field>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="prog-threshold" label={mode === 'points' ? 'Pontos para a recompensa' : 'Visitas para a recompensa'} error={errors.rewardThreshold?.message}>
          <Input id="prog-threshold" inputMode="numeric" {...form.register('rewardThreshold')} />
        </Field>
        <Field id="prog-value" label="Valor da recompensa" hint="Só para controle." error={errors.rewardValue?.message}>
          <MaskedInput mask="money" id="prog-value" {...form.register('rewardValue')} />
        </Field>
      </div>
      <Field id="prog-desc" label="Recompensa" error={errors.rewardDescription?.message}>
        <Input id="prog-desc" maxLength={200} {...form.register('rewardDescription')} />
      </Field>
      <div>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Salvando…' : 'Salvar programa'}
        </Button>
      </div>
    </form>
  );
}
